import { useState } from "react";
import { Camera, X, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import api from "../../utils/api";

const API = `/api`;

function ConditionPhotos({ job, onUpdate, openLightbox, onDeletePhoto }) {
  const [uploading, setUploading] = useState(null);
  const [stagedPhotos, setStagedPhotos] = useState({ pre_work: [], post_work: [] });

  const handlePhotoSelect = (type, files) => {
    if (!files || files.length === 0) return;
    
    const currentCount = type === 'pre_work' ? (job.pre_work_photos?.length || 0) : (job.post_work_photos?.length || 0);
    const stagedCount = stagedPhotos[type].length;
    const remaining = 10 - currentCount - stagedCount;
    
    if (remaining <= 0) {
      toast.error("Maximum 10 photos per section");
      return;
    }
    
    const fileArray = Array.from(files).slice(0, remaining);
    const newStaged = fileArray.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));
    
    setStagedPhotos(prev => ({
      ...prev,
      [type]: [...prev[type], ...newStaged]
    }));
    
    toast.success(`${newStaged.length} photo(s) ready - click "Upload" when done`);
  };

  const removeStagedPhoto = (type, index) => {
    setStagedPhotos(prev => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index)
    }));
  };

  const uploadStagedPhotos = async (type) => {
    if (stagedPhotos[type].length === 0) return;
    
    setUploading(type);
    
    try {
      // Upload photos one at a time to avoid payload size limits
      let uploadedCount = 0;
      
      for (const { file } of stagedPhotos[type]) {
        try {
          // Compress image before upload (smaller for mobile)
          const base64 = await compressImage(file, 800, 0.6);
          
          // Upload single photo
          await api.patch(`${API}/admin/jobs/${job.id}/condition-photos`, {
            photos: [base64],
            photo_type: type
          });
          uploadedCount++;
        } catch (uploadErr) {
          console.error("Single photo upload error:", uploadErr);
          if (uploadedCount === 0) {
            throw uploadErr;
          }
          toast.error(`Uploaded ${uploadedCount}/${stagedPhotos[type].length} photos`);
          break;
        }
      }
      
      // Clear staged and revoke URLs
      stagedPhotos[type].forEach(({ preview }) => URL.revokeObjectURL(preview));
      setStagedPhotos(prev => ({ ...prev, [type]: [] }));
      
      if (uploadedCount > 0) {
        toast.success(`${type === 'pre_work' ? 'Pre-work' : 'Post-work'}: ${uploadedCount} photo(s) uploaded`);
        onUpdate();
      }
    } catch (err) {
      console.error("Upload error:", err);
      toast.error(err.response?.data?.detail || "Failed to upload - try fewer photos");
    } finally {
      setUploading(null);
    }
  };

  // Helper function to compress images
  const compressImage = (file, maxWidth, quality) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.onload = (e) => {
        const img = new window.Image();
        img.onerror = () => reject(new Error("Failed to load image"));
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          const base64 = canvas.toDataURL('image/jpeg', quality);
          resolve(base64);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const prePhotos = job.pre_work_photos || [];
  const postPhotos = job.post_work_photos || [];
  const preStaged = stagedPhotos.pre_work;
  const postStaged = stagedPhotos.post_work;

  return (
    <div className="grid grid-cols-2 gap-4">
      {/* Pre-Work Photos */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs tracking-[0.15em] uppercase text-zinc-500 font-body flex items-center gap-2">
            <Camera size={12} /> Pre-Work ({prePhotos.length}/10)
          </span>
          <div className="flex gap-1">
            {prePhotos.length + preStaged.length < 10 && (
              <label className="px-2 py-1 text-xs uppercase font-body border border-[#f59e0b]/30 text-[#f59e0b] hover:bg-[#f59e0b]/10 transition-colors cursor-pointer flex items-center gap-1">
                <Upload size={10} /> Add
                <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => handlePhotoSelect('pre_work', e.target.files)} />
              </label>
            )}
            {preStaged.length > 0 && (
              <button
                onClick={() => uploadStagedPhotos('pre_work')}
                disabled={uploading === 'pre_work'}
                className="px-2 py-1 text-xs uppercase font-body bg-[#f59e0b] text-white border-none hover:bg-[#f59e0b]/80 transition-colors cursor-pointer disabled:opacity-50"
              >
                {uploading === 'pre_work' ? '...' : `Upload (${preStaged.length})`}
              </button>
            )}
          </div>
        </div>
        {/* Staged photos preview */}
        {preStaged.length > 0 && (
          <div className="flex gap-1 flex-wrap mb-2">
            {preStaged.map((p, i) => (
              <div key={i} className="relative">
                <div className="w-14 h-14 border-2 border-dashed border-[#f59e0b]/50 overflow-hidden">
                  <img src={p.preview} alt={`Staged ${i+1}`} className="w-full h-full object-cover" />
                </div>
                <button
                  onClick={() => removeStagedPhoto('pre_work', i)}
                  className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full cursor-pointer border-none flex items-center justify-center"
                >
                  <X size={8} />
                </button>
              </div>
            ))}
          </div>
        )}
        {/* Existing photos */}
        {prePhotos.length > 0 ? (
          <div className="flex gap-1 flex-wrap">
            {prePhotos.map((p, i) => (
              <div key={i} className="relative group">
                <button onClick={() => openLightbox(prePhotos, i)} className="w-14 h-14 border border-[#f59e0b]/30 hover:border-[#f59e0b] transition-colors cursor-pointer bg-transparent p-0 overflow-hidden">
                  <img src={p} alt={`Pre ${i+1}`} className="w-full h-full object-cover" />
                </button>
                <button
                  onClick={() => onDeletePhoto(job.id, "pre_work", i)}
                  className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer border-none flex items-center justify-center"
                >
                  <X size={8} />
                </button>
              </div>
            ))}
          </div>
        ) : preStaged.length === 0 && (
          <div className="bg-white/5 border border-dashed border-white/10 p-4 text-center">
            <p className="text-zinc-700 text-xs">No pre-work photos</p>
          </div>
        )}
      </div>

      {/* Post-Work Photos */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs tracking-[0.15em] uppercase text-zinc-500 font-body flex items-center gap-2">
            <Camera size={12} /> Post-Work ({postPhotos.length}/10)
          </span>
          <div className="flex gap-1">
            {postPhotos.length + postStaged.length < 10 && (
              <label className="px-2 py-1 text-xs uppercase font-body border border-[#22c55e]/30 text-[#22c55e] hover:bg-[#22c55e]/10 transition-colors cursor-pointer flex items-center gap-1">
                <Upload size={10} /> Add
                <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => handlePhotoSelect('post_work', e.target.files)} />
              </label>
            )}
            {postStaged.length > 0 && (
              <button
                onClick={() => uploadStagedPhotos('post_work')}
                disabled={uploading === 'post_work'}
                className="px-2 py-1 text-xs uppercase font-body bg-[#22c55e] text-white border-none hover:bg-[#22c55e]/80 transition-colors cursor-pointer disabled:opacity-50"
              >
                {uploading === 'post_work' ? '...' : `Upload (${postStaged.length})`}
              </button>
            )}
          </div>
        </div>
        {/* Staged photos preview */}
        {postStaged.length > 0 && (
          <div className="flex gap-1 flex-wrap mb-2">
            {postStaged.map((p, i) => (
              <div key={i} className="relative">
                <div className="w-14 h-14 border-2 border-dashed border-[#22c55e]/50 overflow-hidden">
                  <img src={p.preview} alt={`Staged ${i+1}`} className="w-full h-full object-cover" />
                </div>
                <button
                  onClick={() => removeStagedPhoto('post_work', i)}
                  className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full cursor-pointer border-none flex items-center justify-center"
                >
                  <X size={8} />
                </button>
              </div>
            ))}
          </div>
        )}
        {/* Existing photos */}
        {postPhotos.length > 0 ? (
          <div className="flex gap-1 flex-wrap">
            {postPhotos.map((p, i) => (
              <div key={i} className="relative group">
                <button onClick={() => openLightbox(postPhotos, i)} className="w-14 h-14 border border-[#22c55e]/30 hover:border-[#22c55e] transition-colors cursor-pointer bg-transparent p-0 overflow-hidden">
                  <img src={p} alt={`Post ${i+1}`} className="w-full h-full object-cover" />
                </button>
                <button
                  onClick={() => onDeletePhoto(job.id, "post_work", i)}
                  className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer border-none flex items-center justify-center"
                >
                  <X size={8} />
                </button>
              </div>
            ))}
          </div>
        ) : postStaged.length === 0 && (
          <div className="bg-white/5 border border-dashed border-white/10 p-4 text-center">
            <p className="text-zinc-700 text-xs">No post-work photos</p>
          </div>
        )}
      </div>
    </div>
  );
}


export { ConditionPhotos };
